/**
 * The stamp app's event vocabulary.
 *
 * Every logged event name lives here, so the whole vocabulary stays reviewable in one place and
 * call sites import a constant instead of retyping a string literal. `buildEventLog` refuses names
 * that are not registered, which turns a typo into a dropped event with a warning rather than a
 * near-duplicate name nobody notices in the collection.
 *
 * Names are snake_case `<entity>_<verb>`, shared with the analytics dashboard where an event means
 * the same thing in both apps (`login`, `filter`). `operator_*` covers the shift workers selected on
 * the device, as opposed to `user`, which in this app means the shared Firebase account.
 */

/** Distinguishes this app's rows from the dashboard's in the shared `event_logs` collection. */
export const EVENT_SOURCE = 'stamp_app';

export const EVENT_NAMES = Object.freeze({
  // Session
  LOGIN: 'login',

  // Stamping
  STAMP_START: 'stamp_start',
  STAMP_STOP: 'stamp_stop',
  STAMP_EXTEND: 'stamp_extend',
  STAMP_TIMEOUT_SHOWN: 'stamp_timeout_shown',
  SHIFT_TIMEOUT_SHOWN: 'shift_timeout_shown',

  // Button layout
  LAYOUT_EDIT_START: 'layout_edit_start',
  LAYOUT_SAVE: 'layout_save',

  // Device and operator
  DEVICE_SWITCH: 'device_switch',
  OPERATOR_SWITCH: 'operator_switch',
  OPERATOR_ADD: 'operator_add',
  OPERATOR_DELETE: 'operator_delete',

  // Recipe control
  CORRECTION_CONFIRM: 'correction_confirm',
  INSTRUCTION_CONFIRM: 'instruction_confirm',
  PRODUCTION_DETAILS_OPEN: 'production_details_open',

  // Shared with the dashboard
  FILTER: 'filter',
});

const KNOWN_EVENT_NAMES = new Set(Object.values(EVENT_NAMES));

/**
 * Whether a name belongs to the registry above.
 *
 * @param {string} eventName - Candidate event name.
 * @returns {boolean} True when the name is registered.
 */
export function isKnownEventName(eventName) {
  return KNOWN_EVENT_NAMES.has(eventName);
}
